/**
 * @file Pickup component: a powerup lying on the floor.
 *
 * Holds the definition it will apply plus its idle animation phase. What the
 * definition *means* is decided by {@link ../systems/PickupSystem.js}, keyed on
 * `definition.kind`.
 */

import { Component } from '../ecs/Component.js';

export class Pickup extends Component {
  /**
   * @param {string} key Key into {@link ../config/Powerups.js POWERUPS}.
   * @param {import('../config/Powerups.js').PowerupDefinition} definition The powerup data.
   * @param {import('./Weapon.js').Weapon | null} [weapon] For a weapon drop, the
   *   very stat block that will be equipped — carried rather than rebuilt, so a
   *   gun keeps whatever state it was dropped with.
   */
  constructor(key, definition, weapon = null) {
    super();
    /** @type {string} */
    this.key = key;
    /** @type {import('../config/Powerups.js').PowerupDefinition} */
    this.definition = definition;
    /** @type {import('./Weapon.js').Weapon | null} */
    this.weapon = weapon;
    /** @type {number} Random phase so a cluster of drops does not bob in lockstep. */
    this.phase = Math.random() * Math.PI * 2;
    /**
     * Per-instance weapon state carried by a dropped weapon, so ammo (once it
     * exists) and the current cooldown belong to *this* gun rather than to its
     * archetype. Null for every other kind of pickup.
     * @type {import('./Weapon.js').WeaponSnapshot | null}
     */
    this.weaponState = null;
    /**
     * False while the pickup is still under the player who dropped it. It arms
     * the moment they step out of range, otherwise standing on a swapped-out
     * weapon would ping-pong the two guns forever.
     * @type {boolean}
     */
    this.armed = true;
    /**
     * Whether {@link ../systems/PickupSystem.js} has already checked this drop
     * for the depleted-weapon red tint. A floor weapon's ammo never changes
     * while it sits there, so the check only needs to run once.
     * @type {boolean}
     */
    this.depletedTintChecked = false;
  }

  /** @returns {void} */
  reset() {
    this.phase = Math.random() * Math.PI * 2;
    this.weaponState = null;
    this.armed = true;
    this.depletedTintChecked = false;
  }
}
